import React, { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { Spin } from 'antd'
import { colors } from '../../../helper/Helper'

const GroupedBarChart = React.memo(({ graph_id = '', graph_data = [] }) => {

    const [isLoading, setIsLoading] = useState(true)

    const xAxis = graph_data
        .map((item) => {
            return Object.keys(item)
                ?.filter((key) => key?.startsWith('xAxis_'))
                ?.map((key) => item[key])
        })
        .flat()

    const dynamicSeries = () => {
        const uniqueKeys = Array.from(
            new Set(
                graph_data?.flatMap((item) =>
                    Object.keys(item)?.filter((key) => !key?.startsWith('xAxis_'))
                )
            )
        )
        return uniqueKeys.map((key) => ({
            name: key?.replace(/yAxis/g, '')?.replace(/_/g, ' ')?.replace(/^\w/, (c) => c.toUpperCase()),
            data: graph_data?.map((item) => item[key]),
        }))
    }

    const chartData = dynamicSeries()

    const calculateBarWidth = (data) => {
        if (data[0]?.data?.length < 5) {
            return '15%'
        } else {
            return '40%'
        }
    }

    const calculateChartHeight = (dataLength) => {
        if (dataLength <= 12) {
            return '600'
        }
        else if (dataLength > 12 && dataLength < 20) {
            return '700'
        }
        else if (dataLength > 21 && dataLength < 31) {
            return '1000'
        } else if (dataLength > 30 && dataLength <= 60) {
            return '2000'
        } else if (dataLength > 60 && dataLength <= 90) {
            return '3000'
        } else {
            return '5000'
        }
    }

    const isHorizontal = chartData[0]?.data?.length > 20
    const chartHeight = chartData[0]?.data ? calculateChartHeight(chartData[0]?.data?.length) : '500'

    const chartOptions = {
        chart: {
            id: graph_id || 'grouped_bar_chart',
            zoom: {
                enabled: false,
            },
            toolbar: {
                show: false,
            }
        },
        plotOptions: {
            bar: {
                horizontal: isHorizontal,
                barHeight: '60%',
                columnWidth: calculateBarWidth(chartData)
            }
        },
        xaxis: {
            categories: xAxis,
            labels: {
                formatter: function simplifyValue(value) {

                    if (value >= 10000000) {
                        return value / 10000000 + ' Cr'
                    } else if (value >= 100000 && value < 10000000) {
                        return value / 100000 + ' L'
                    } else if (value >= 10000 && value < 100000) {
                        return value / 1000 + ' K'
                    } else if (value >= 1000 && value < 10000) {
                        return value / 1000 + ' K'
                    } else {
                        return value
                    }
                },
                style: {
                    colors: '#b8b8b8',
                    fontSize: '10px',
                    fontWeight: 500,
                },
            },
            tickAmount: 10,
        },
        yaxis: {
            labels: {
                formatter: function simplifyValue(value) {
                    if (value.length > 12) {
                        return value.substring(0, 12) + '...'
                    }

                    if (value >= 10000000) {
                        return value / 10000000 + ' Cr'
                    } else if (value >= 100000 && value < 10000000) {
                        return value / 100000 + ' L'
                    } else if (value >= 10000 && value < 100000) {
                        return value / 1000 + ' K'
                    } else if (value >= 1000 && value < 10000) {
                        return value / 1000 + ' K'
                    } else {
                        return value
                    }
                },
                style: {
                    colors: '#b8b8b8',
                    fontSize: '10px',
                    fontWeight: 500,
                },
            },
        },
        colors: colors,
        legend: {
            show: true,
            fontSize: '10px',
            position: "top",
            labels: {
                useSeriesColors: true,
            },
            formatter: function (seriesName) {
                return seriesName.replace('YAxis', '')
            },

            markers: {
                size: 5,
                strokeWidth: 0,
                shape: 'circle',
            },
        },
        tooltip: {
            shared: true,
            intersect: false,
            theme: 'dark',
            style: {
                fontSize: '12px',
            },
            x: {
                formatter: function (value) {
                    return value
                },
            },
            y: {
                formatter: function simplifyValue(value) {
                    if (value >= 10000000) {
                        return (value / 10000000).toFixed(2) + ' Cr'
                    } else if (value >= 100000 && value < 10000000) {
                        return (value / 100000).toFixed(2) + ' L'
                    } else if (value >= 10000 && value < 100000) {
                        return (value / 1000).toFixed(2) + ' K'
                    } else if (value >= 1000 && value < 10000) {
                        return (value / 1000).toFixed(2) + ' K'
                    } else {
                        return value
                    }
                },
            },
        },
        grid: {
            show: false,
            borderColor: '#e5e5e5',
        },
        dataLabels: {
            enabled: false,
        },
    }

    useEffect(() => {
        if (chartData.length) {
            const timeout = setTimeout(() => setIsLoading(false), 500)
            return () => clearTimeout(timeout)
        }
    }, [chartData])

    return (
        <div
            style={{
                overflow: "hidden",
                width: '100%',
                height: "100%"
            }}
        >
            {isLoading ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin size='large' />
                </div>
            ) : (
                <div
                    style={{
                        height: "100%",
                        width: "100%",
                        overflow: "scroll",
                        whiteSpace: "nowrap",
                        paddingTop: "45px"
                    }}
                >
                    <Chart
                        options={chartOptions}
                        series={chartData}
                        type="bar"
                        width="100%"
                        height={chartHeight}
                    />
                </div>
            )}
        </div>
    )
})

export default GroupedBarChart